import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import { MdAdd, MdDelete, MdSecurity } from "react-icons/md";
import { createApi } from "../../../../core/api";

export const RolePermissionCrud = () => {
  const api = useMemo(() => createApi(), []);
  const { setPageMenus } = useOutletContext<any>();

  const [data, setData] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [roleId, setRoleId] = useState<number | "">("");
  const [permissionId, setPermissionId] = useState<number | "">("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rp, r, p]: any = await Promise.all([
        api.rolePermission.getAll(),
        api.Role.getAll(),
        api.permission.getAll(),
      ]);

      if (rp?.ok) setData(rp.data?.data ?? rp.data ?? []);
      if (r?.ok) setRoles(r.data?.data ?? r.data ?? []);
      if (p?.ok) setPermissions(p.data?.data ?? p.data ?? []);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Role Permissions",
        items: [
          { label: "Role Permissions", route: "/dashboard/role-permission" },
        ],
      },
    ]);
    load();
    return () => setPageMenus([]);
  }, [load, setPageMenus]);

  const openCreate = () => {
    setRoleId("");
    setPermissionId("");
    setError(null);
    setOpen(true);
  };

  const submit = async () => {
    if (!roleId || !permissionId || saving) return;

    setSaving(true);
    setError(null);

    try {
      const r = await api.rolePermission.create({
        roleId,
        permissionId,
      });
      if (!r?.ok) throw new Error(r?.message || "Create failed");

      setOpen(false);
      load();
    } catch (e: any) {
      setError(e?.message || "Unexpected error occurred");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setDeleteError(null);
  };

  const doDelete = async () => {
    if (!deleteId || deleting) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      const r = await api.rolePermission.delete(deleteId);
      if (!r?.ok) throw new Error(r?.message || "Delete failed");

      setDeleteId(null);
      load();
    } catch (e: any) {
      setDeleteError(e?.message || "Unexpected error occurred");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<any[]>(
    () => [
      {
        key: "id",
        title: "ID",
        minWidth: 80,
        accessor: (r: any) => r.id,
      },
      {
        key: "role",
        title: "Role",
        minWidth: 260,
        accessor: (r: any) => roles.find((x) => x.id === r.roleId)?.name ?? "",
        render: (_: any, v: string) => (
          <Badge label={v} color="#2563EB" bg="rgba(37,99,235,.15)" />
        ),
      },
      {
        key: "permission",
        title: "Permission",
        minWidth: 320,
        accessor: (r: any) => r.permission.key ?? "",
        render: (_: any, v: string) => (
          <Badge
            icon={MdSecurity}
            label={v}
            color="#0EA5E9"
            bg="rgba(14,165,233,.15)"
          />
        ),
      },
      {
        key: "permission-desc",
        title: "Desciription",
        minWidth: 320,
        accessor: (r: any) => r.permission.description ?? "",
        render: (_: any, v: string) => (
          <Badge
            icon={MdSecurity}
            label={v}
            color="#0EA5E9"
            bg="rgba(14,165,233,.15)"
          />
        ),
      },
      {
        key: "actions",
        title: "Actions",
        minWidth: 160,
        render: (row: any) => (
          <GroupButton
            items={[
              {
                key: "delete",
                icon: MdDelete,
                color: "#DC2626",
                onClick: () => confirmDelete(row.id),
              },
            ]}
          />
        ),
      },
    ],
    [roles, permissions]
  );

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Role Permissions</div>
          <div style={S.subTitle}>Assign permissions to roles</div>
        </div>

        <Button variant="primary" icon={MdAdd} onClick={openCreate}>
          Add Role Permission
        </Button>
      </div>

      <Table
        tableKey="role-permission"
        loading={loading}
        columns={columns}
        data={data}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />

      <Modal open={open} onClose={() => (!saving ? setOpen(false) : null)}>
        <div style={S.modalTitle}>Add Role Permission</div>

        <div style={S.form}>
          <label style={S.label}>Role</label>
          <select
            value={roleId}
            onChange={(e) => setRoleId(Number(e.target.value))}
            style={S.input}
            disabled={saving}
          >
            <option value="">Select role</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>

          <label style={S.label}>Permission</label>
          <select
            value={permissionId}
            onChange={(e) => setPermissionId(Number(e.target.value))}
            style={S.input}
            disabled={saving}
          >
            <option value="">Select permission</option>
            {permissions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.description}
              </option>
            ))}
          </select>

          {error && <div style={S.error}>{error}</div>}
        </div>

        <div style={S.actions}>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={saving}>
            Save
          </Button>
        </div>
      </Modal>

      <Modal
        open={!!deleteId}
        onClose={() => (!deleting ? setDeleteId(null) : null)}
      >
        <div style={S.modalTitle}>Delete Role Permission</div>

        <div style={{ fontSize: 14, color: "#334155" }}>
          Are you sure you want to delete this role permission?
        </div>

        {deleteError && <div style={S.error}>{deleteError}</div>}

        <div style={S.actions}>
          <Button variant="ghost" onClick={() => setDeleteId(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={doDelete} loading={deleting}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: { position: "absolute", inset: 0, padding: 20, background: "#F8FAFC" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: { fontSize: 18, fontWeight: 600, color: "#0F172A" },
  subTitle: { fontSize: 13, color: "#64748B" },
  modalTitle: { fontSize: 18, fontWeight: 800, marginBottom: 14 },
  form: { display: "flex", flexDirection: "column", gap: 10 },
  label: { fontSize: 12, fontWeight: 700, color: "#334155" },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  error: { marginTop: 8, fontSize: 12, fontWeight: 600, color: "#DC2626" },
};
